import React, { useEffect, useState } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";

function SprintDialog({
  open,
  sprint,
  isSaving = false,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState({
    sprint_name: "",
    goal: "",
    start_date: "",
    end_date: "",
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    if (sprint) {
      setForm({
        sprint_name: sprint.sprint_name || "",
        goal: sprint.goal || "",
        start_date: sprint.start_date || "",
        end_date: sprint.end_date || "",
      });
    } else {
      setForm({ sprint_name: "", goal: "", start_date: "", end_date: "" });
    }
    setError("");
  }, [open, sprint]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = () => {
    if (!form.sprint_name.trim()) {
      setError("Sprint name is required");
      return;
    }

    onSave({
      sprint_name: form.sprint_name.trim(),
      goal: form.goal || null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      ...(sprint ? { status: sprint.status } : {}),
    });
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {sprint ? "Update Sprint" : "Create Sprint"}
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "background.default" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
              Sprint Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Sprint 1"
              value={form.sprint_name}
              onChange={(e) => setField("sprint_name", e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
              Goal
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder="What should this sprint achieve?"
              value={form.goal}
              onChange={(e) => setField("goal", e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
            />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                Start Date
              </Typography>
              <TextField
                type="date"
                fullWidth
                size="small"
                value={form.start_date || ""}
                onChange={(e) => setField("start_date", e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                End Date
              </Typography>
              <TextField
                type="date"
                fullWidth
                size="small"
                value={form.end_date || ""}
                onChange={(e) => setField("end_date", e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, bgcolor: "background.default" }}>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SprintDialog;
